import { useState, useMemo, useCallback } from 'react';
import { useMutation, useLazyQuery } from '@apollo/client';
import debounce from 'lodash/debounce';
import {
  SIGNUP_WITH_USERNAME,
  LOGIN_WITH_USERNAME,
  CHECK_USERNAME_AVAILABLE,
} from '../queries';
import { validatePassword, validateUsername } from '../utils';
import { type AccessBlockedInfo, type AuthStep } from '../types';

interface UseUsernameAuthParams {
  username: string;
  password: string;
  confirmPassword: string;
  isSignup: boolean;
  setError: (error: string) => void;
  setVerifiedUserId: (id: string | null) => void;
  setVerifiedUserData: (data: any) => void;
  setPendingToken: (token: string | null) => void;
  setShowCaptureDetails: (show: boolean) => void;
  // Optional so every tenant caller compiles: when provided, an ACCESS_BLOCKED login
  // answer switches the modal to the AccessBlockedCard instead of an inline error.
  setAccessBlockedInfo?: (info: AccessBlockedInfo | null) => void;
  setAuthStep?: (step: AuthStep) => void;
  onSuccess: (token: string) => void;
  onClose: () => void;
}

/**
 * Custom hook for username/password authentication logic
 *
 * Handles:
 * - Login with username/password
 * - Signup with username/password
 * - Username availability checking (debounced)
 * - Form validation
 */
export const useUsernameAuth = ({
  username,
  password,
  confirmPassword,
  isSignup,
  setError,
  setVerifiedUserId,
  setVerifiedUserData,
  setPendingToken,
  setShowCaptureDetails,
  setAccessBlockedInfo,
  setAuthStep,
  onSuccess,
  onClose,
}: UseUsernameAuthParams) => {
  // Username availability state
  const [usernameAvailable, setUsernameAvailable] = useState<boolean | null>(null);
  const [checkingUsername, setCheckingUsername] = useState(false);

  // GraphQL mutations
  const [signupWithUsername, { loading: signingUp }] = useMutation(SIGNUP_WITH_USERNAME);
  const [loginWithUsername, { loading: loggingIn }] = useMutation(LOGIN_WITH_USERNAME);
  const [checkUsernameQuery] = useLazyQuery(CHECK_USERNAME_AVAILABLE);

  // Debounced username availability check
  const checkUsernameAvailability = useMemo(
    () =>
      debounce(async (usernameToCheck: string) => {
        // Only check in signup mode and if username is long enough
        if (!isSignup || usernameToCheck.length < 3) {
          setUsernameAvailable(null);
          setCheckingUsername(false);
          return;
        }

        // Validate username format first
        const usernameError = validateUsername(usernameToCheck);
        if (usernameError) {
          setUsernameAvailable(null);
          setCheckingUsername(false);
          return;
        }

        setCheckingUsername(true);
        try {
          const { data } = await checkUsernameQuery({
            variables: { username: usernameToCheck },
          });
          setUsernameAvailable(data?.checkUsernameAvailable ?? null);
        } catch (err) {
          console.error('Error checking username availability:', err);
          setUsernameAvailable(null);
        } finally {
          setCheckingUsername(false);
        }
      }, 500),
    [isSignup, checkUsernameQuery]
  );

  // Cleanup debounce on unmount
  const cancelUsernameCheck = useCallback(() => {
    checkUsernameAvailability.cancel();
  }, [checkUsernameAvailability]);

  // Validate form before submission
  const validateForm = (): string | null => {
    const usernameError = validateUsername(username);
    if (usernameError) return usernameError;

    const passwordError = validatePassword(password);
    if (passwordError) return passwordError;

    if (isSignup) {
      if (!confirmPassword) {
        return 'Please confirm your password';
      }
      if (password !== confirmPassword) {
        return 'Passwords do not match';
      }
      if (usernameAvailable === false) {
        return 'Username is already taken';
      }
    }

    return null;
  };

  // Helper to check if profile needs completion
  const needsProfileCapture = (client: any): boolean => {
    // Check if missing required fields
    const missingName = !client.fName || !client.lName;
    const hasUsername = !!client.username;
    // Username-based accounts don't need email/phone to complete login
    const missingContact = !hasUsername && !client.email && !client.phoneNumber;
    return missingName || missingContact;
  };

  // Handle login
  const handleLogin = async () => {
    setError('');

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      console.log('🔐 [USERNAME-LOGIN-1] Attempting login for:', username);

      const { data } = await loginWithUsername({
        variables: {
          input: {
            username: username.toLowerCase(),
            password,
          },
        },
      });

      if (data?.loginWithUsername) {
        const { token, client } = data.loginWithUsername;
        console.log('✅ [USERNAME-LOGIN-2] Login successful, client:', client.id);

        // Check if we need to capture additional details
        if (needsProfileCapture(client)) {
          console.log('📝 [USERNAME-LOGIN-3] Profile incomplete, showing capture modal');
          setVerifiedUserId(client.id);
          setVerifiedUserData(client);
          setPendingToken(token);
          setShowCaptureDetails(true);
        } else {
          console.log('✅ [USERNAME-LOGIN-3] Profile complete, completing login');
          onSuccess(token);
          onClose();
        }
      }
    } catch (err: any) {
      console.error('❌ [USERNAME-LOGIN-ERROR]', err);

      // Extract error from Apollo Client error structure
      let errorMessage = '';
      let errorCode = '';

      if (err.graphQLErrors && err.graphQLErrors.length > 0) {
        const gqlError = err.graphQLErrors[0];
        errorMessage = gqlError.message || '';
        errorCode = gqlError.extensions?.code || '';
      } else if (err.networkError) {
        errorMessage = 'Network error. Please check your connection.';
      } else {
        errorMessage = err.message || '';
      }

      console.log('📝 [USERNAME-LOGIN-ERROR] Code:', errorCode, 'Message:', errorMessage);

      // Handle specific error codes
      if (errorCode === 'ACCESS_BLOCKED') {
        // Password was right; the account's sign-in is switched off. Card, not an inline error.
        if (setAccessBlockedInfo && setAuthStep) {
          setAccessBlockedInfo({ identifier: username, method: 'username' });
          setAuthStep('access-blocked');
        } else {
          setError(errorMessage || 'Sign-in for this account has been switched off.');
        }
      } else if (errorCode === 'USER_NOT_FOUND' || errorMessage.includes('No account exists')) {
        setError("No account exists with this username. Please create an account first.");
      } else if (errorCode === 'NO_PASSWORD' || errorMessage.includes('created via phone or email')) {
        setError("This account was created via phone or email. Please use those methods to sign in.");
      } else if (errorCode === 'INVALID_PASSWORD' || errorMessage.includes('incorrect')) {
        setError("The password you entered is incorrect.");
      } else if (errorCode === 'BAD_USER_INPUT' || errorMessage.includes('Argument Validation Error')) {
        setError("Please enter a valid username and password.");
      } else {
        setError(errorMessage || 'Unable to sign in. Please try again.');
      }
    }
  };

  // Handle signup
  const handleSignup = async () => {
    setError('');

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      console.log('🔐 [USERNAME-SIGNUP-1] Attempting signup for:', username);

      const { data } = await signupWithUsername({
        variables: {
          input: {
            username: username.toLowerCase(),
            password,
          },
        },
      });

      if (data?.signupWithUsername) {
        const { token, client } = data.signupWithUsername;
        console.log('✅ [USERNAME-SIGNUP-2] Signup successful, client:', client.id);

        // New signups will need profile capture (name, email/phone)
        console.log('📝 [USERNAME-SIGNUP-3] Showing profile capture for new user');
        setVerifiedUserId(client.id);
        setVerifiedUserData(client);
        setPendingToken(token);
        setShowCaptureDetails(true);
      }
    } catch (err: any) {
      console.error('❌ [USERNAME-SIGNUP-ERROR]', err);

      // Extract error message from Apollo Client error structure
      let errorMessage = '';
      if (err.graphQLErrors && err.graphQLErrors.length > 0) {
        errorMessage = err.graphQLErrors[0].message || '';
      } else if (err.networkError) {
        errorMessage = err.networkError.message || 'Network error';
      } else {
        errorMessage = err.message || '';
      }

      console.log('📝 [USERNAME-SIGNUP-ERROR] Parsed error message:', errorMessage);

      if (errorMessage.includes('already taken') || errorMessage.includes('already exists')) {
        setError('Username is already taken');
        setUsernameAvailable(false);
      } else {
        // Extract message after colon if present
        const cleanMessage = errorMessage.includes(':')
          ? errorMessage.split(':').slice(1).join(':').trim()
          : errorMessage;
        setError(cleanMessage || 'Failed to create account. Please try again.');
      }
    }
  };

  // Main submit handler
  const handleSubmit = () => {
    if (isSignup) {
      handleSignup();
    } else {
      handleLogin();
    }
  };

  return {
    // State
    usernameAvailable,
    checkingUsername,
    loading: signingUp || loggingIn,

    // Actions
    checkUsernameAvailability,
    cancelUsernameCheck,
    handleLogin,
    handleSignup,
    handleSubmit,
    validateForm,
  };
};
